/**
 * Utilidades y constantes para QuestionTotals
 */

// Array de traducciones de meses
export const MONTH_NAMES = [
    'date.january',
    'date.february',
    'date.march',
    'date.april',
    'date.may',
    'date.june',
    'date.july',
    'date.august',
    'date.september',
    'date.october',
    'date.november',
    'date.december'
];

// Keys a excluir del procesamiento
export const EXCLUDED_KEYS = ['price', 'dates', 'total', 'month-*'];

/**
 * Extrae datos del array de values
 * @param {Array} values - Array de items { key: value }
 * @returns {{ selectedDays: Array, hasMonthlyData: boolean, monthsData: Object }}
 */
export const extraerDatosDeValues = (values) => {
    let selectedDays = [];
    let hasMonthlyData = false;
    let monthsData = {};

    values.forEach((valueItem) => {
        Object.keys(valueItem).forEach((key) => {
            if (key === 'dates') {
                selectedDays = valueItem[key];
            }
            if (key?.startsWith('month-')) {
                hasMonthlyData = true;
                const month = key.replace('month-', '');
                monthsData[month] = valueItem[key];
            }
        });
    });

    return { selectedDays, hasMonthlyData, monthsData };
};

/**
 * Valida que haya precios disponibles para la cantidad de días
 * @param {Object} settings - Configuración con prices
 * @param {number} countSelectedDays - Cantidad de días seleccionados
 * @param {boolean} useRoomCategory - Si se usa categoría de habitación
 * @param {string} roomCategory - Código de categoría (ej: "DBL", "SGL")
 * @returns {{ isValid: boolean, invalidCount: number, invalidMonth: string | null }}
 */
export const validarPreciosDisponibles = (
    settings,
    countSelectedDays,
    useRoomCategory = false,
    roomCategory = null,
    monthsData = null
) => {
    if (!settings?.prices) {
        return { isValid: true, invalidCount: 0, invalidMonth: null };
    }

    // Validación para datos mensuales
    if (monthsData && Object.keys(monthsData).length > 0) {
        for (const [month, daysInMonth] of Object.entries(monthsData)) {
            const monthKey = `month-${month}`;
            const pricesArray = settings.prices[monthKey];

            // ✅ CORREGIDO: Si el mes NO tiene precios O tiene menos precios que días seleccionados
            if (!pricesArray || pricesArray.length < daysInMonth) {
                return {
                    isValid: false,
                    invalidCount: pricesArray?.length || 0,
                    invalidMonth: monthKey
                };
            }
        }
        return { isValid: true, invalidCount: 0, invalidMonth: null };
    }

    // Validación para useRoomCategory
    if (useRoomCategory && roomCategory) {
        const prices = settings.prices?.[roomCategory.toUpperCase()];
        if (!prices || prices?.length < 1) {
            return {
                isValid: false,
                invalidCount: prices?.length || 0,
                invalidMonth: null
            };
        }
        return { isValid: true, invalidCount: 0, invalidMonth: null };
    }

    // Validación estándar para otros items
    for (const key of Object.keys(settings.prices)) {
        if (key === 'price' || key === 'dates') {
            continue;
        }
        if (settings.prices[key].length < countSelectedDays) {
            return {
                isValid: false,
                invalidCount: settings.prices[key].length,
                invalidMonth: null
            };
        }
    }

    return { isValid: true, invalidCount: 0, invalidMonth: null };
};

/**
 * Obtiene el nombre traducido de un mes
 * @param {number} monthNumber - Número del mes (1-12)
 * @param {Function} T - Función de traducción
 * @returns {string} Nombre del mes traducido
 */
export const getMonthName = (monthNumber, T) => {
    const monthIndex = parseInt(monthNumber) - 1;
    if (monthIndex < 0 || monthIndex >= 12) {
        return '';
    }
    return T(MONTH_NAMES[monthIndex]);
};

/**
 * Obtiene el texto de "día" o "días"
 * @param {number} count - Cantidad
 * @param {Function} T - Función de traducción
 * @returns {string}
 */
export const getDayText = (count, T) => {
    return count === 1 ? T('general.day') : T('general.days');
};

/**
 * Obtiene el precio de un array de precios
 * @param {Array} pricesArray - Array de precios
 * @param {number} index - Índice (basado en cantidad - 1)
 * @returns {number} Precio o 0 si no existe
 */
export const obtenerPrecio = (pricesArray, index) => {
    if (!pricesArray || !Array.isArray(pricesArray) || index < 0) {
        return 0;
    }
    return pricesArray[index] || 0;
};

/**
 * Crea un item de desglose
 * @param {string} label - Etiqueta del item
 * @param {number} quantity - Cantidad
 * @param {number} price - Precio
 * @param {number} subtotal - Subtotal
 * @returns {Object} Item formateado
 */
export const crearItem = (label, quantity, price, subtotal) => {
    return {
        label,
        quantity,
        price,
        subtotal
    };
};

/**
 * Obtiene la cantidad seleccionada desde values, compatible con key "qty" o cualquier
 * pregunta de tipo select/pax-qty-selector (p. ej. number_of_dogs).
 * @param {Array} values - Array de items del formulario, ej. [{ qty: "2" }] o [{ number_of_dogs: "2" }]
 * @param {Object|string} settings - settings del ítem con settings.questions (objeto o JSON string)
 * @returns {number} Cantidad (entero >= 0)
 */
export const getQuantityFromValues = (values, settings) => {
    if (!values?.length || !values[0]) return 0;
    const first = values[0];
    if (first.qty !== undefined && first.qty !== null && first.qty !== '') {
        const n = parseInt(first.qty, 10);
        return Number.isNaN(n) ? 0 : Math.max(0, n);
    }
    const s = typeof settings === 'string'
        ? (() => { try { return JSON.parse(settings); } catch { return null; } })()
        : settings;
    const questions = s?.questions || [];
    const qtyQuestion = questions.find(
        (q) => (q.type === 'select' || q.type === 'pax-qty-selector') && q.key
    );
    if (qtyQuestion && first[qtyQuestion.key] !== undefined && first[qtyQuestion.key] !== null && first[qtyQuestion.key] !== '') {
        const n = parseInt(first[qtyQuestion.key], 10);
        return Number.isNaN(n) ? 0 : Math.max(0, n);
    }
    return 0;
};

/**
 * Calcula si el botón Comprar/Reservar debe estar deshabilitado para un settings de upselling.
 * Misma lógica que Settings.js (useEffect Ramas 1-5) para que web check-in y página dedicada se comporten igual.
 * @param {Array} values - Array de values (mismo formato que en Settings)
 * @param {Object|string} settings - settings con questions (objeto o JSON string)
 * @returns {boolean} true = botón deshabilitado
 */
export const getSettingsBookButtonDisabled = (values, settings) => {
    const s = typeof settings === 'string'
        ? (() => { try { return JSON.parse(settings); } catch { return null; } })()
        : settings;
    if (!s?.questions) return true;

    const auxValues = Array.isArray(values) ? [...values] : [];
    const hasKeyTotal = auxValues.find(item => item['total'] !== undefined);
    const hasKeyDates = auxValues.find(item => item['dates'] !== undefined);
    let hasQty = auxValues.find(item => item['qty'] !== undefined);
    const qtyFromHelper = getQuantityFromValues(auxValues, s);
    if (!hasQty && qtyFromHelper > 0) hasQty = { qty: qtyFromHelper };

    const paxQtySelectorQuestions = s.questions.filter(q => q.type === 'pax-qty-selector') || [];
    const hasPaxQtySelector = paxQtySelectorQuestions.length > 0;
    const hasQuestionTotals = s.questions.some(q => q.type === 'totals');
    const hasDateSelector = s.questions.some(q => q.type === 'date-selector');

    let isPaxQtyValid = !hasPaxQtySelector;
    if (hasPaxQtySelector) {
        const isMultiple = paxQtySelectorQuestions.length > 1;
        if (isMultiple) {
            const hasAtLeastOne = paxQtySelectorQuestions.some(q => {
                const keys = [q.key, q.label].filter(Boolean);
                return auxValues.some(item => keys.some(k => item[k] !== undefined && Number(item[k]) > 0));
            });
            if (!hasAtLeastOne) {
                isPaxQtyValid = false;
            } else {
                isPaxQtyValid = paxQtySelectorQuestions.every(q => {
                    const possibleQtyKeys = [q.key, q.label].filter(Boolean);
                    let paxQtyValue = null;
                    let usedKey = null;
                    for (const it of auxValues) {
                        for (const k of possibleQtyKeys) {
                            if (it[k] !== undefined && Number(it[k]) > 0) {
                                paxQtyValue = it;
                                usedKey = k;
                                break;
                            }
                        }
                        if (paxQtyValue) break;
                    }
                    if (!paxQtyValue || !usedKey || paxQtyValue[usedKey] <= 0) return true;
                    const requiredPax = Number(paxQtyValue[usedKey]) || 0;
                    const passengersItem = auxValues.find(it => it?.passengers && (it.passengers[usedKey] !== undefined || it.passengers[q.key] !== undefined));
                    const assignedPax = passengersItem
                        ? (passengersItem.passengers[usedKey]?.length ?? passengersItem.passengers[q.key]?.length ?? 0)
                        : 0;
                    return assignedPax === requiredPax;
                });
            }
        } else {
            isPaxQtyValid = paxQtySelectorQuestions.every(q => {
                const possibleQtyKeys = [q.key, q.label].filter(Boolean);
                let paxQtyValue = null;
                let usedKey = null;
                for (const it of auxValues) {
                    for (const k of possibleQtyKeys) {
                        if (it[k] !== undefined && Number(it[k]) > 0) {
                            paxQtyValue = it;
                            usedKey = k;
                            break;
                        }
                    }
                    if (paxQtyValue) break;
                }
                if (!paxQtyValue || !usedKey) return false;
                const requiredPax = Number(paxQtyValue[usedKey]) || 0;
                const passengersItem = auxValues.find(it => it?.passengers && (it.passengers[usedKey] !== undefined || it.passengers[q.key] !== undefined));
                const assignedPax = passengersItem
                    ? (passengersItem.passengers[usedKey]?.length ?? passengersItem.passengers[q.key]?.length ?? 0)
                    : 0;
                return assignedPax === requiredPax;
            });
        }
    }

    // Rama 1: total válido
    if (hasKeyTotal && hasKeyTotal.total > 0) {
        return hasPaxQtySelector ? !isPaxQtyValid : false;
    }
    // Rama 2: dates válidas
    if (hasKeyDates && hasKeyDates.dates && hasKeyDates.dates.length > 0) {
        if (!hasQuestionTotals) {
            return hasPaxQtySelector ? !isPaxQtyValid : false;
        }
        return true; // con QuestionTotals pero sin total aún
    }
    // Rama 3: qty válida
    if (hasQty && hasQty.qty && parseInt(hasQty.qty, 10) > 0) {
        if (hasPaxQtySelector) return !isPaxQtyValid;
        if (hasDateSelector && !hasKeyDates) return true;
        return false;
    }
    // Rama 4: solo pax-qty con valor
    const hasPaxValue = auxValues.some(item => {
        const keys = Object.keys(item);
        return keys.some(k => paxQtySelectorQuestions.some(q => q.key === k || q.label === k));
    });
    if (hasPaxQtySelector && hasPaxValue) {
        if (hasDateSelector && !hasKeyDates) return true;
        return !isPaxQtyValid;
    }
    // Rama 5: nada válido
    return true;
};

